import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Wrench, CheckCircle2 } from 'lucide-react';
import { getApiBaseUrl } from '../../utils/apiConfig';
import BraspressConfigModal from './BraspressConfigModal';
import JamefConfigModal from './JamefConfigModal';
import MovviConfigModal from './MovviConfigModal';
import '../users-customers/components/UsersCustomersView.css';
import './IntegracoesPage.css';

const ROUTE_LOGISTICA = '/configuracoes/integracoes/1';
const ROUTE_AREA_TECNICA = '/configuracoes/integracoes/2';

const CATEGORIES = [
  { id: 1, path: ROUTE_LOGISTICA, label: 'Logística e estoque', icon: Package },
  { id: 2, path: ROUTE_AREA_TECNICA, label: 'Área Técnica', icon: Wrench }
];

const publicUrl = process.env.PUBLIC_URL || '';

const LOGISTICA_INTEGRATIONS = [
  {
    id: 'braspress',
    title: 'Braspress',
    subtitle: 'Rastreio de encomendas',
    integrationKey: 'braspress',
    logoSrc: `${publicUrl}/braspress_logo.png`
  },
  {
    id: 'jamef',
    title: 'Jamef',
    subtitle: 'Rastreio de encomendas',
    integrationKey: 'jamef',
    logoSrc: `${publicUrl}/jamef_logo.png`
  },
  {
    id: 'movvi',
    title: 'Movvi',
    subtitle: 'Rastreio de encomendas',
    integrationKey: 'movvi',
    logoSrc: `${publicUrl}/movvi_logo.png`
  }
];

function IntegracoesPage({ categoryId }) {
  const navigate = useNavigate();
  const safeId = categoryId === 2 ? 2 : 1;
  const apiBaseUrl = getApiBaseUrl();

  const [braspressModalOpen, setBraspressModalOpen] = useState(false);
  const [braspressConfigured, setBraspressConfigured] = useState(false);
  const [jamefModalOpen, setJamefModalOpen] = useState(false);
  const [jamefConfigured, setJamefConfigured] = useState(false);
  const [movviModalOpen, setMovviModalOpen] = useState(false);
  const [movviConfigured, setMovviConfigured] = useState(false);

  const refreshBraspressStatus = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token || safeId !== 1) return;
    try {
      const res = await fetch(`${apiBaseUrl}/api/integracoes/braspress`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && json.success && json.data) {
        setBraspressConfigured(!!json.data.configured);
      }
    } catch (_) {
      /* silencioso: card sem check se falhar */
    }
  }, [apiBaseUrl, safeId]);

  const refreshJamefStatus = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token || safeId !== 1) return;
    try {
      const res = await fetch(`${apiBaseUrl}/api/integracoes/jamef`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && json.success && json.data) {
        setJamefConfigured(!!json.data.configured);
      }
    } catch (_) {
      /* silencioso */
    }
  }, [apiBaseUrl, safeId]);

  const refreshMovviStatus = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token || safeId !== 1) return;
    try {
      const res = await fetch(`${apiBaseUrl}/api/integracoes/movvi`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && json.success && json.data) {
        setMovviConfigured(!!(json.data.configured ?? json.data.habilitado));
      }
    } catch (_) {
      /* silencioso */
    }
  }, [apiBaseUrl, safeId]);

  useEffect(() => {
    refreshBraspressStatus();
    refreshJamefStatus();
    refreshMovviStatus();
  }, [refreshBraspressStatus, refreshJamefStatus, refreshMovviStatus]);

  const openBraspress = () => setBraspressModalOpen(true);
  const openJamef = () => setJamefModalOpen(true);
  const openMovvi = () => setMovviModalOpen(true);

  const onCardKeyDown = (e, integrationKey) => {
    if (integrationKey !== 'braspress' && integrationKey !== 'jamef' && integrationKey !== 'movvi') return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (integrationKey === 'braspress') openBraspress();
      else if (integrationKey === 'jamef') openJamef();
      else openMovvi();
    }
  };

  return (
    <div className="integracoes-config-wrap customers-config-root">
      <aside className="customers-module-sidebar" aria-label="Categorias de integrações">
        <nav className="customers-module-nav">
          {CATEGORIES.map(({ id, path, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              className={`customers-module-item ${safeId === id ? 'active' : ''}`}
              onClick={() => navigate(path)}
            >
              <Icon size={20} strokeWidth={1.75} className="customers-module-icon" aria-hidden />
              <span className="customers-module-label">{label}</span>
            </button>
          ))}
        </nav>
      </aside>

      <div className="customers-module-content">
        {safeId === 1 && (
          <>
            <header className="integracoes-main-header">
              <h1 className="integracoes-main-title">Logística e estoque</h1>
              <p className="integracoes-main-desc">
                Controle, organização e acompanhamento dos envios e entregas.
              </p>
            </header>
            <ul className="integracoes-grid">
              {LOGISTICA_INTEGRATIONS.map((item) => {
                const isBraspress = item.integrationKey === 'braspress';
                const isJamef = item.integrationKey === 'jamef';
                const isMovvi = item.integrationKey === 'movvi';
                const isClickable = isBraspress || isJamef || isMovvi;
                const showSaved =
                  (isBraspress && braspressConfigured) ||
                  (isJamef && jamefConfigured) ||
                  (isMovvi && movviConfigured);
                return (
                  <li
                    key={item.id}
                    className={`integracoes-card ${isClickable ? 'integracoes-card--clickable' : ''}`}
                    {...(isClickable
                      ? {
                          role: 'button',
                          tabIndex: 0,
                          onClick: () => {
                            if (isBraspress) openBraspress();
                            else if (isJamef) openJamef();
                            else openMovvi();
                          },
                          onKeyDown: (e) => onCardKeyDown(e, item.integrationKey)
                        }
                      : {})}
                  >
                    {showSaved && (
                      <span className="integracoes-card-saved" title="Integração configurada" aria-hidden>
                        <CheckCircle2 size={20} strokeWidth={2} className="integracoes-card-saved-icon" />
                      </span>
                    )}
                    <div
                      className={`integracoes-card-icon ${item.logoSrc ? 'integracoes-card-icon--logo' : ''}`}
                      aria-hidden
                    >
                      {item.logoSrc ? (
                        <img
                          src={item.logoSrc}
                          alt=""
                          className="integracoes-card-icon-img"
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        item.title.slice(0, 1)
                      )}
                    </div>
                    <div className="integracoes-card-body">
                      <div className="integracoes-card-title">{item.title}</div>
                      <div className="integracoes-card-subtitle">{item.subtitle}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}

        {safeId === 2 && (
          <>
            <header className="integracoes-main-header">
              <h1 className="integracoes-main-title">Área Técnica</h1>
              <p className="integracoes-main-desc">
                Integrações voltadas ao fluxo técnico e operação de campo.
              </p>
            </header>
            <div className="integracoes-empty">
              <p>Nenhuma integração disponível nesta categoria por enquanto.</p>
            </div>
          </>
        )}
      </div>

      <BraspressConfigModal
        open={braspressModalOpen}
        onClose={() => setBraspressModalOpen(false)}
        apiBaseUrl={apiBaseUrl}
        onSaved={refreshBraspressStatus}
      />
      <JamefConfigModal
        open={jamefModalOpen}
        onClose={() => setJamefModalOpen(false)}
        apiBaseUrl={apiBaseUrl}
        onSaved={refreshJamefStatus}
      />
      <MovviConfigModal
        open={movviModalOpen}
        onClose={() => setMovviModalOpen(false)}
        apiBaseUrl={apiBaseUrl}
        onSaved={refreshMovviStatus}
      />
    </div>
  );
}

export default IntegracoesPage;
